import { ParseError } from './types';

/**
 * A small, non-validating XML parser.
 *
 * Written rather than borrowed because `DOMParser` **does not exist in a Web
 * Worker**. A worker has no DOM, so the moment KML, KMZ and GPX parsing moved
 * off the main thread — which is the only way a fifty-file drop of 3.5 MB
 * exports stays responsive — every XML format stopped working, while still
 * passing every unit test, because jsdom provides a `DOMParser` and a browser
 * worker does not. That divergence is the bug this file removes: one parser,
 * identical in the browser, in the worker and in jsdom, so a golden hash means
 * the same thing everywhere.
 *
 * It handles what these formats actually contain and nothing else: elements,
 * attributes, text, CDATA, comments, processing instructions, `DOCTYPE`,
 * self-closing tags, and the five predefined entities plus numeric references.
 * It does not do validation, DTDs, entity declarations or namespace resolution
 * — the callers match on local name, which reads every prefix a writer might
 * choose and is what `DATA-SOURCES.md` §1's "parse defensively" needs.
 *
 * It is also lenient in the two ways real files are broken: an unclosed element
 * simply stays open to the end of the document, and an unmatched closing tag is
 * ignored. A truncated download then yields the fixes it does contain, and the
 * caller decides whether that is enough.
 */

/**
 * The local name of a text run.
 *
 * Text is a node rather than a string field on its parent, because that is the
 * only way to keep mixed content in order: `<a>one<b>two</b>three</a>` has to
 * read as `onetwothree`, and an element that stored its own text separately
 * from its children could only ever produce `onethreetwo`. No element can be
 * called this, so matching by local name never returns one by accident.
 */
export const TEXT_NODE = '#text';

export interface XmlNode {
  /**
   * Name without its namespace prefix: `coord` for `gx:coord`. `#text` for a
   * text run.
   */
  localName: string;
  /** Name as written, prefix included. Kept for diagnostics. */
  tagName: string;
  attributes: Readonly<Record<string, string>>;
  children: XmlNode[];
  /** The content of a text run; always `''` for an element. */
  text: string;
}

const ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
};

/**
 * Decode the entities XML defines, and leave every other one alone.
 *
 * Leaving unknown entities as written is deliberate. Flightradar24's
 * descriptions contain `&deg;`, which is an HTML entity and not an XML one, and
 * the KML parser's `htmlToText` is what knows about it. Silently dropping it
 * here would take the degree sign out of a heading and leave the number
 * unreadable.
 */
export function decodeXmlEntities(text: string): string {
  if (!text.includes('&')) {
    return text;
  }
  return text.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, body: string) => {
    if (body.startsWith('#x') || body.startsWith('#X')) {
      const code = Number.parseInt(body.slice(2), 16);
      return Number.isFinite(code) && code > 0 ? String.fromCodePoint(code) : match;
    }
    if (body.startsWith('#')) {
      const code = Number.parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code > 0 ? String.fromCodePoint(code) : match;
    }
    return ENTITIES[body.toLowerCase()] ?? match;
  });
}

function localNameOf(name: string): string {
  const colon = name.indexOf(':');
  return colon === -1 ? name : name.slice(colon + 1);
}

/**
 * The index of the `>` that closes the tag starting at `from`.
 *
 * Scans past `>` inside a quoted attribute value, which a plain `indexOf`
 * would stop at — a GPX `<trkpt lat="-37.9" desc="a > b">` is enough to need it.
 */
function findTagEnd(text: string, from: number): number {
  let quote = '';
  for (let i = from + 1; i < text.length; i += 1) {
    const char = text[i];
    if (quote !== '') {
      if (char === quote) {
        quote = '';
      }
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      continue;
    }
    if (char === '>') {
      return i;
    }
  }
  return -1;
}

const ATTRIBUTE = /([^\s=/]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/g;

function parseStartTag(body: string): XmlNode {
  const nameMatch = /^[^\s/>]+/.exec(body);
  const tagName = nameMatch?.[0] ?? '';
  const attributes: Record<string, string> = {};

  ATTRIBUTE.lastIndex = tagName.length;
  let match: RegExpExecArray | null;
  while ((match = ATTRIBUTE.exec(body)) !== null) {
    const key = match[1];
    if (key !== undefined) {
      attributes[key] = decodeXmlEntities(match[2] ?? match[3] ?? match[4] ?? '');
    }
  }

  return {
    localName: localNameOf(tagName),
    tagName,
    attributes,
    children: [],
    text: '',
  };
}

/**
 * Parse XML into a tree.
 *
 * @returns the document node, whose children are the top-level elements.
 * @throws ParseError when the input contains no element at all.
 */
export function parseXmlTree(source: string): XmlNode {
  // A UTF-8 BOM before the declaration is common and is not markup.
  const text = source.charCodeAt(0) === 0xfeff ? source.slice(1) : source;

  const document: XmlNode = {
    localName: '#document',
    tagName: '#document',
    attributes: {},
    children: [],
    text: '',
  };
  const stack: XmlNode[] = [document];
  let elementCount = 0;
  let index = 0;

  const current = (): XmlNode => stack[stack.length - 1] ?? document;

  /*
   * Whitespace-only runs are dropped. They are almost all of the text in an
   * indented 3.5 MB KML, no caller ever wants them (`textOf` trims), and
   * keeping them would roughly double the node count for nothing.
   */
  const addText = (value: string): void => {
    if (value === '' || value.trim() === '') {
      return;
    }
    current().children.push({
      localName: TEXT_NODE,
      tagName: TEXT_NODE,
      attributes: {},
      children: [],
      text: value,
    });
  };

  while (index < text.length) {
    const open = text.indexOf('<', index);

    if (open === -1) {
      addText(decodeXmlEntities(text.slice(index)));
      break;
    }
    if (open > index) {
      addText(decodeXmlEntities(text.slice(index, open)));
    }

    if (text.startsWith('<!--', open)) {
      const end = text.indexOf('-->', open + 4);
      index = end === -1 ? text.length : end + 3;
      continue;
    }
    if (text.startsWith('<![CDATA[', open)) {
      const end = text.indexOf(']]>', open + 9);
      // CDATA is raw by definition: no entity decoding, which is exactly why
      // FR24's `&deg;` arrives intact for `htmlToText` to deal with.
      addText(text.slice(open + 9, end === -1 ? text.length : end));
      index = end === -1 ? text.length : end + 3;
      continue;
    }
    if (text.startsWith('<?', open)) {
      const end = text.indexOf('?>', open + 2);
      index = end === -1 ? text.length : end + 2;
      continue;
    }
    if (text.startsWith('<!', open)) {
      // DOCTYPE and friends. An internal subset in square brackets has to be
      // stepped over as a unit, or its `>` ends the declaration early.
      const bracket = text.indexOf('[', open);
      const gt = text.indexOf('>', open);
      if (bracket !== -1 && gt !== -1 && bracket < gt) {
        const close = text.indexOf(']', bracket);
        const after = close === -1 ? -1 : text.indexOf('>', close);
        index = after === -1 ? text.length : after + 1;
      } else {
        index = gt === -1 ? text.length : gt + 1;
      }
      continue;
    }

    const close = findTagEnd(text, open);
    if (close === -1) {
      // A tag cut off mid-way. Nothing usable follows it.
      break;
    }
    const inner = text.slice(open + 1, close);

    if (inner.startsWith('/')) {
      const name = localNameOf(inner.slice(1).trim());
      // Unwind to the matching ancestor. An unmatched close tag leaves the
      // stack alone rather than closing something it did not open.
      for (let depth = stack.length - 1; depth > 0; depth -= 1) {
        if (stack[depth]?.localName === name) {
          stack.length = depth;
          break;
        }
      }
      index = close + 1;
      continue;
    }

    const selfClosing = inner.endsWith('/');
    const node = parseStartTag(selfClosing ? inner.slice(0, -1) : inner);
    if (node.tagName === '') {
      index = close + 1;
      continue;
    }

    current().children.push(node);
    elementCount += 1;
    if (!selfClosing) {
      stack.push(node);
    }
    index = close + 1;
  }

  if (elementCount === 0) {
    throw new ParseError(
      'This file is not XML. If it came out of an export, try exporting it again.',
    );
  }

  return document;
}
